import { DateFormatter } from '@repo/toolkit/date'

import { ApplicationStatusEnum } from '../../types/enums/enrollment/application-status.enum'
import { EnrollmentInformationStatus } from '../../types/enums/enrollment/enrollment-information-status.enum'
import { MedicalConditionEnum } from '../../types/enums/enrollment/medical-condition-enum'
import { MedicalStatusEnum } from '../../types/enums/enrollment/medical-status.enum'
import { PaymentStatusEnum } from '../../types/enums/enrollment/payment-status.enum'
import { OrigineEnum } from '../../types/enums/information-requests/origin.enum'
import { AccomodationCategoryEnum } from '../../types/enums/product/accomodation-category.enum'
import { EnrollmentFilter } from '../../types/models/enrollment/enrollment-filter'

export const transformEnrollmentsFiltersToUrl = (filters?: EnrollmentFilter): string => {
  if (!filters) return ''
  const searchParams = new URLSearchParams()

  if (filters.periods && filters.periods.length > 0)
    searchParams.set('periods', filters.periods.join(','))
  if (filters.departureDate) searchParams.set('departureDate', filters.departureDate)
  if (filters.country) searchParams.set('country', filters.country)
  if (filters.formulas && filters.formulas.length > 0)
    searchParams.set('formulas', filters.formulas.join(','))
  if (filters.options && filters.options.length > 0)
    searchParams.set('options', filters.options.join(','))
  if (filters.tripPlaces && filters.tripPlaces.length > 0)
    searchParams.set('tripPlaces', filters.tripPlaces.join(','))
  if (filters.transportMean) searchParams.set('transportMean', filters.transportMean.toString())
  if (filters.statuses && filters.statuses.length > 0)
    searchParams.set('statuses', filters.statuses.join(','))
  if (filters.paymentStatus) searchParams.set('paymentStatus', filters.paymentStatus)
  if (filters.medicalLevel) searchParams.set('medicalLevel', filters.medicalLevel)
  if (filters.medicalStatus) searchParams.set('medicalStatus', filters.medicalStatus)
  if (filters.createdAt) searchParams.set('createdAt', filters.createdAt)
  if (filters.source) searchParams.set('source', filters.source)
  if (filters.origin) searchParams.set('origin', filters.origin)
  if (filters.attributedUserId)
    searchParams.set('attributedUserId', filters.attributedUserId.toString())
  if (filters.centerIds && filters.centerIds.length > 0)
    searchParams.set('centerIds', filters.centerIds.join(','))
  if (filters.tripDateId) searchParams.set('tripDateId', filters.tripDateId.toString())
  if (filters.dispatched) searchParams.set('dispatched', filters.dispatched.toString())
  if (filters.tripBookingId) searchParams.set('tripBookingId', filters.tripBookingId.toString())

  if (filters.departureDateAfter)
    searchParams.set('departureDateAfter', filters.departureDateAfter)
  if (filters.departureDateBefore)
    searchParams.set('departureDateBefore', filters.departureDateBefore)
  if (filters.returnDateAfter) searchParams.set('returnDateAfter', filters.returnDateAfter)
  if (filters.returnDateBefore) searchParams.set('returnDateBefore', filters.returnDateBefore)

  if (filters.accomodations && filters.accomodations.length > 0)
    searchParams.set('accomodations', filters.accomodations.join(','))

  if (filters.confirmedAfter) searchParams.set('confirmedAfter', filters.confirmedAfter)
  if (filters.confirmedBefore) searchParams.set('confirmedBefore', filters.confirmedBefore)

  if (filters.isAssigned) searchParams.set('isAssigned', filters.isAssigned.toString())
  if (filters.assignedAfter) searchParams.set('assignedAfter', filters.assignedAfter)
  if (filters.assignedBefore) searchParams.set('assignedBefore', filters.assignedBefore)

  if (filters.isPaymentFullyPaid)
    searchParams.set('isPaymentFullyPaid', filters.isPaymentFullyPaid.toString())
  if (filters.enrollmentProfileStatus)
    searchParams.set('enrollmentProfileStatus', filters.enrollmentProfileStatus)
  if (filters.isIdentityDocumentsComplete)
    searchParams.set('isIdentityDocumentsComplete', filters.isIdentityDocumentsComplete.toString())
  if (filters.isHealthDocumentsComplete)
    searchParams.set('isHealthDocumentsComplete', filters.isHealthDocumentsComplete.toString())
  if (filters.isTestComplete) searchParams.set('isTestComplete', filters.isTestComplete.toString())
  if (filters.isIndividualTravelInfoComplete)
    searchParams.set(
      'isIndividualTravelInfoComplete',
      filters.isIndividualTravelInfoComplete.toString()
    )
  if (filters.isAA) searchParams.set('isAA', filters.isAA.toString())

  return searchParams.toString()
}

export const transformUrlToEnrollmentFilters = (
  searchParams: URLSearchParams
): EnrollmentFilter => {
  const filters: EnrollmentFilter = {}

  const periods = searchParams.get('periods')
  if (periods) filters.periods = periods.split(',')

  const departureDate = DateFormatter.checkDateFormat(searchParams.get('departureDate'))
  if (departureDate) filters.departureDate = departureDate
  const country = searchParams.get('country')
  if (country) filters.country = country
  const formulas = searchParams.get('formulas')
  if (formulas) filters.formulas = formulas.split(',')
  const options = searchParams.get('options')
  if (options) filters.options = options.split(',').map(Number)
  const tripPlaces = searchParams.get('tripPlaces')
  if (tripPlaces) filters.tripPlaces = tripPlaces.split(',')
  const transportMean = searchParams.get('transportMean')
  if (transportMean) filters.transportMean = +transportMean
  const statuses = searchParams.get('statuses')
  if (statuses) filters.statuses = statuses.split(',') as ApplicationStatusEnum[]
  const paymentStatus = searchParams.get('paymentStatus')
  if (paymentStatus) filters.paymentStatus = paymentStatus as PaymentStatusEnum
  const medicalLevel = searchParams.get('medicalLevel')
  if (medicalLevel) filters.medicalLevel = medicalLevel as MedicalConditionEnum
  const medicalStatus = searchParams.get('medicalStatus')
  if (medicalStatus) filters.medicalStatus = medicalStatus as MedicalStatusEnum
  const createdAt = DateFormatter.checkDateFormat(searchParams.get('createdAt'))
  if (createdAt) filters.createdAt = createdAt
  const source = searchParams.get('source')
  if (source) filters.source = source
  const origin = searchParams.get('origin')
  if (origin) filters.origin = origin as OrigineEnum
  const attributedUserId = searchParams.get('attributedUserId')
  if (attributedUserId) filters.attributedUserId = +attributedUserId
  const centerIds = searchParams.get('centerIds')
  if (centerIds) filters.centerIds = centerIds.split(',').map(Number)
  const tripDateId = searchParams.get('tripDateId')
  if (tripDateId) filters.tripDateId = +tripDateId
  const dispatched = searchParams.get('dispatched')
  if (dispatched) filters.dispatched = dispatched === 'true'
  const tripBookingId = searchParams.get('tripBookingId')
  if (tripBookingId) filters.tripBookingId = +tripBookingId

  const departureDateAfter = searchParams.get('departureDateAfter')
  if (departureDateAfter) filters.departureDateAfter = departureDateAfter
  const departureDateBefore = searchParams.get('departureDateBefore')
  if (departureDateBefore) filters.departureDateBefore = departureDateBefore
  const returnDateAfter = searchParams.get('returnDateAfter')
  if (returnDateAfter) filters.returnDateAfter = returnDateAfter
  const returnDateBefore = searchParams.get('returnDateBefore')
  if (returnDateBefore) filters.returnDateBefore = returnDateBefore

  const accomodations = searchParams.get('accomodations')
  if (accomodations) filters.accomodations = accomodations.split(',') as AccomodationCategoryEnum[]

  const confirmedAfter = searchParams.get('confirmedAfter')
  if (confirmedAfter) filters.confirmedAfter = confirmedAfter
  const confirmedBefore = searchParams.get('confirmedBefore')
  if (confirmedBefore) filters.confirmedBefore = confirmedBefore

  const isAssigned = searchParams.get('isAssigned')
  if (isAssigned) filters.isAssigned = isAssigned === 'true'
  const assignedAfter = searchParams.get('assignedAfter')
  if (assignedAfter) filters.assignedAfter = assignedAfter
  const assignedBefore = searchParams.get('assignedBefore')
  if (assignedBefore) filters.assignedBefore = assignedBefore

  const isPaymentFullyPaid = searchParams.get('isPaymentFullyPaid')
  if (isPaymentFullyPaid) filters.isPaymentFullyPaid = isPaymentFullyPaid === 'true'
  const enrollmentProfileStatus = searchParams.get('enrollmentProfileStatus')
  if (enrollmentProfileStatus)
    filters.enrollmentProfileStatus = enrollmentProfileStatus as EnrollmentInformationStatus
  const isIdentityDocumentsComplete = searchParams.get('isIdentityDocumentsComplete')
  if (isIdentityDocumentsComplete)
    filters.isIdentityDocumentsComplete = isIdentityDocumentsComplete === 'true'
  const isHealthDocumentsComplete = searchParams.get('isHealthDocumentsComplete')
  if (isHealthDocumentsComplete)
    filters.isHealthDocumentsComplete = isHealthDocumentsComplete === 'true'
  const isTestComplete = searchParams.get('isTestComplete')
  if (isTestComplete) filters.isTestComplete = isTestComplete === 'true'
  const isIndividualTravelInfoComplete = searchParams.get('isIndividualTravelInfoComplete')
  if (isIndividualTravelInfoComplete)
    filters.isIndividualTravelInfoComplete = isIndividualTravelInfoComplete === 'true'
  const isAA = searchParams.get('isAA')
  if (isAA) filters.isAA = isAA === 'true'

  return filters
}
